import { logger } from './logger';
import { createContactInformationFields, createDocumentsFields, createEnvironmentsFields, createTechnicalDetailsFields, SCHEMA_ID_TO_STEP_ERROR_MAP } from './securityReviewValidatorUtilities';
import { checkStepCompleted, hasErrorInStep } from './validatorUtilities';
export class SecurityReviewValidator {
  constructor(securityReview) {
    this._securityReview = void 0;
    this._stepsCompleted = void 0;
    this.securityReview = securityReview;
  }
  set securityReview(securityReview) {
    if (securityReview !== this._securityReview) {
      this._securityReview = securityReview;
    }
  }
  get securityReview() {
    return this._securityReview;
  }
  get numberOfSteps() {
    return 5;
  }
  get numberOfStepsWithFields() {
    return 4; //Omit the 'Review & Submit' step
  }
  get stepsCompleted() {
    if (!this._securityReview) {
      return [];
    }

    // Since computing the number of steps completed is an expensive operation,
    // cache them and invalidate if the security review is reassigned.
    if (this._stepsCompleted) {
      return this._stepsCompleted;
    }
    this._stepsCompleted = [];
    for (let i = 1; i <= this.numberOfStepsWithFields; i++) {
      if (checkStepCompleted(this.getFieldsForStep(i.toString()))) {
        this._stepsCompleted.push(i);
      }
    }
    return this._stepsCompleted;
  }
  get isValid() {
    return this.stepsCompleted.length === this.numberOfSteps;
  }
  resetCachedStepsCompleted() {
    this._stepsCompleted = undefined;
  }
  getFieldsForStep(numberOfStep) {
    // TODO: implement the method for each step
    switch (numberOfStep) {
      case '1':
        return createContactInformationFields(this._securityReview);
      case '2':
        return createTechnicalDetailsFields();
      case '3':
        return createDocumentsFields(this._securityReview);
      case '4':
        return createEnvironmentsFields(this._securityReview);
      default:
        return [];
    }
  }
  getStepFields() {
    // TODO: implement the method for each step
    const result = {
      '1': createContactInformationFields(this._securityReview),
      '2': createTechnicalDetailsFields(),
      '3': createDocumentsFields(this._securityReview),
      '4': createEnvironmentsFields(this._securityReview)
    };
    return result;
  }
  getStepsWithErrors(errors, returnOnFirstError = true) {
    const stepList = this.getStepFields();
    const steps = Object.keys(stepList);
    const stepsWithErrors = new Set();
    const schemaStepErrorMap = SCHEMA_ID_TO_STEP_ERROR_MAP;

    // Here's an example of a possible error stack: instance.extensions[0].data.extensions[0].data.installUrl does not conform to the \"uri\" format
    for (const error of errors) {
      var _matches$groups$index, _matches$groups;
      const stack = Array.isArray(error.path) ? error.path[error.path.length - 1] : error.stack;
      const name = stack === 'data' ? error.argument || '' : stack;
      for (const step of steps) {
        if (stepsWithErrors.has(step)) {
          continue;
        }
        if (hasErrorInStep(stepList[step], name)) {
          stepsWithErrors.add(step);
          if (returnOnFirstError) {
            return stepsWithErrors;
          }
        }
      }

      // Look for plugins
      let matchType = error.stack;
      const matches = error.stack.match(/plugins*\[(?<index>[0-9]+)\]/);
      const pluginIndex = parseInt((_matches$groups$index = matches === null || matches === void 0 ? void 0 : (_matches$groups = matches.groups) === null || _matches$groups === void 0 ? void 0 : _matches$groups.index) !== null && _matches$groups$index !== void 0 ? _matches$groups$index : '-1');
      if (pluginIndex !== -1) {
        var _this$_securityReview;
        const plugin = (_this$_securityReview = this._securityReview.plugins) === null || _this$_securityReview === void 0 ? void 0 : _this$_securityReview[pluginIndex];
        if (!plugin || !plugin.pluginType) {
          logger.error(`Failed to find security review plugin with index=${pluginIndex}.`);
          continue;
        }
        matchType = plugin.pluginType;
      } else {
        matchType = error.stack;
      }
      const step = schemaStepErrorMap[matchType];
      if (step && steps.indexOf(step) === -1) {
        logger.error(`Step ${step} that has an error was not included on the steps of the security review.`);
        continue;
      }
      if (step && !stepsWithErrors[step]) {
        stepsWithErrors.add(step);
        if (returnOnFirstError) {
          return stepsWithErrors;
        }
        continue;
      }
      logger.error(`Found a validation error ${matchType}, but was missing an entry for it in STEP_ERROR_MAP.`);
    }
    return stepsWithErrors;
  }
}